import { useState } from 'react';
import { toLabelledText, type TemplateRow, type TemplateVersionRow } from '@nest-recruit/shared';
import { isSameDraft, rebasedDraft } from '../../../../lib/draft-rebase';

/** What the editor holds, in labelled form (`{{Job title}}`). */
export interface TemplateDraft {
  readonly name: string;
  readonly subject: string;
  readonly body: string;
}

const EMPTY_DRAFT: TemplateDraft = { name: '', subject: '', body: '' };

export function draftOf(template: Pick<TemplateRow, 'name' | 'subject' | 'bodyText'> | null): TemplateDraft {
  if (!template) return EMPTY_DRAFT;
  return { name: template.name, subject: toLabelledText(template.subject), body: toLabelledText(template.bodyText) };
}

function sameWording(a: TemplateRow, b: TemplateRow): boolean {
  return a.name === b.name && a.subject === b.subject && a.bodyText === b.bodyText;
}

export interface TemplateDraftState {
  readonly draft: TemplateDraft;
  /** The saved version the draft started from: what the save is checked against (review W7). */
  readonly base: TemplateRow | null;
  readonly isDirty: boolean;
  /** Someone else saved new wording while this draft was being changed: the save would be refused. */
  readonly theirs: TemplateRow | null;
  readonly setField: (field: keyof TemplateDraft, value: string) => void;
  /** Their wording as the new base, keeping each field this person changed (review W3). */
  readonly takeTheirs: () => void;
  readonly adopt: (saved: TemplateRow) => void;
  /** "Use this wording" on an earlier version: its subject and message, not yet saved. */
  readonly useWording: (version: TemplateVersionRow) => void;
}

/**
 * ADMIN-09's draft (Admin redesign batch 2). It starts from the template as it was opened and keeps that as its base.
 * When the server's copy moves on: a change that left the wording alone (switched on or off) is taken silently; new
 * wording replaces an untouched draft; and an edited draft keeps the person's words and says someone else saved.
 */
export function useTemplateDraft(latest: TemplateRow | null): TemplateDraftState {
  const [base, setBase] = useState<TemplateRow | null>(latest);
  const [draft, setDraft] = useState<TemplateDraft>(() => draftOf(latest));
  const isDirty = latest === null ? !isSameDraft(draft, EMPTY_DRAFT) : !isSameDraft(draft, draftOf(base));

  // A newer copy that changes nothing typed here follows at once (derived during render, review W7): the wording left
  // alone (switched on or off), nothing typed yet, or typed to read just like it (our own save, refetched first).
  if (latest && base && latest.version !== base.version) {
    if (sameWording(latest, base) || isSameDraft(draft, draftOf(latest))) {
      setBase(latest);
    } else if (!isDirty) {
      setBase(latest);
      setDraft(draftOf(latest));
    }
  }

  const theirs = latest && base && latest.version !== base.version && !sameWording(latest, base) && isDirty && !isSameDraft(draft, draftOf(latest)) ? latest : null;

  return {
    draft,
    base,
    isDirty,
    theirs,
    setField: (field, value) => setDraft((current) => ({ ...current, [field]: value })),
    takeTheirs: () => {
      if (!theirs) return;
      setDraft(rebasedDraft(draft, draftOf(base), draftOf(theirs)));
      setBase(theirs);
    },
    adopt: (saved) => {
      setBase(saved);
      setDraft(draftOf(saved));
    },
    useWording: (version) => setDraft((current) => ({ ...current, subject: toLabelledText(version.subject), body: toLabelledText(version.bodyText ?? '') })),
  };
}
